import { useState } from "react";
import { useDispatch } from "react-redux";
import { AdminActions } from "src/redux/Admin/actions";
import { ConfirmActions } from "src/redux/Confirm/actions";
import useUnsavedChangesGuard from "src/utils/useUnsavedChangesGuard";

const actionVerb = { enable: "enable", disable: "disable", delete: "delete" };

/**
 * Staged creates, enable/disable/delete and role changes for a set of users,
 * applied through a single confirm/discard bar (no per-row dialogs), with the
 * standard unsaved-changes nav guard. Shared by the platform-users page and the
 * per-cpty users page. `onApplied` is called after a successful apply so the
 * caller can reload its data.
 *
 * `createUser(form, reason)` is the page's own create call, since a platform
 * supervisor and a counterparty user are made through different endpoints.
 */
export default function useStagedUserChanges(onApplied, createUser) {
    const dispatch = useDispatch();
    // userId -> { user, role }: role switches staged in the selector.
    const [pendingRoles, setPendingRoles] = useState(new Map());
    // userId -> { user, kind: "enable" | "disable" | "delete" }.
    const [pendingActions, setPendingActions] = useState(new Map());
    // Users typed into the create form and not yet applied, oldest first. They
    // have no id until the server makes them, so they are keyed by a counter.
    const [pendingCreates, setPendingCreates] = useState([]);

    const clearStaged = () => {
        setPendingRoles(new Map());
        setPendingActions(new Map());
        setPendingCreates([]);
    };

    // Stage a user to be created. The key is local to this batch and never
    // reaches the server.
    const onStageCreate = (form) => {
        setPendingCreates((prev) => [
            ...prev,
            { ...form, stagedKey: `staged-${prev.length}-${form.email}` },
        ]);
    };

    const onUnstageCreate = (stagedKey) => {
        setPendingCreates((prev) => prev.filter((c) => c.stagedKey !== stagedKey));
    };

    // Stage an enable/disable toggle; auto-clears if it lands back on the
    // user's current state.
    const onToggleEnabled = (user) => {
        setPendingActions((prev) => {
            const next = new Map(prev);
            const cur = next.get(user.id);
            const effective = cur?.kind === "enable" ? true : cur?.kind === "disable" ? false : user.enabled;
            const desired = !effective;
            if (desired === user.enabled) next.delete(user.id);
            else next.set(user.id, { user, kind: desired ? "enable" : "disable" });
            return next;
        });
    };

    // Stage / un-stage a delete (toggle).
    const onDelete = (user) => {
        setPendingActions((prev) => {
            const next = new Map(prev);
            if (next.get(user.id)?.kind === "delete") next.delete(user.id);
            else next.set(user.id, { user, kind: "delete" });
            return next;
        });
    };

    const onChangeRole = (user, role) => {
        setPendingRoles((prev) => {
            const next = new Map(prev);
            if (!role || (user.roles || []).includes(role)) {
                next.delete(user.id);
            } else {
                next.set(user.id, { user, role });
            }
            return next;
        });
    };

    const pendingCount = pendingRoles.size + pendingActions.size + pendingCreates.length;
    const summary = [
        ...pendingCreates.map((c) => `${c.email}: create${c.role ? ` as ${c.role}` : ""}`),
        ...[...pendingActions.values()].map(({ user, kind }) => `${user.email}: ${actionVerb[kind]}`),
        ...[...pendingRoles.values()].map(({ user, role }) => `${user.email}: role -> ${role}`),
    ].join("\n");

    useUnsavedChangesGuard(pendingCount > 0, summary);

    // One confirm for the whole batch (deletes are destructive), then apply:
    // deletes, enable/disable, and role changes. No per-row dialogs.
    const applyChanges = () => {
        const actions = [...pendingActions.values()];
        const roles = [...pendingRoles.values()];
        const creates = [...pendingCreates];
        dispatch(ConfirmActions.Open({
            title: "Apply User Changes",
            message: `Apply ${pendingCount} change${pendingCount > 1 ? "s" : ""}:\n${summary}`,
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Reason for these user changes",
            okTitle: "Apply",
            cancelTitle: "Cancel",
            warning: actions.some((a) => a.kind === "delete"),
            onOk: async (reason) => {
                for (const create of creates) {
                    try {
                        await createUser(create, reason);
                    } catch (e) {
                        console.error("Failed to create", create.email, e);
                    }
                }
                for (const { user, kind } of actions) {
                    try {
                        if (kind === "delete") {
                            await dispatch(AdminActions.DeleteUser(user.realm, user.id, reason));
                        } else {
                            await dispatch(AdminActions.SetUserEnabled(user.realm, user.id, kind === "enable", reason));
                        }
                    } catch (e) {
                        console.error("Failed to apply action for", user.email, e);
                    }
                }
                for (const { user, role } of roles) {
                    // A user staged for deletion skips the role change.
                    if (pendingActions.get(user.id)?.kind === "delete") continue;
                    try {
                        await dispatch(AdminActions.SetCptyUserRole(user.id, role, reason));
                    } catch (e) {
                        console.error("Failed to change role for", user.email, e);
                    }
                }
                clearStaged();
                if (onApplied) onApplied();
            },
        }));
    };

    return {
        pendingRoles,
        pendingActions,
        pendingCreates,
        onStageCreate,
        onUnstageCreate,
        onToggleEnabled,
        onDelete,
        onChangeRole,
        applyChanges,
        clearStaged,
        pendingCount,
        summary,
    };
}
